// src/errors/ErrorRouteContext.jsx
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";

const ErrorRouteContext = createContext(null);

/**
 * Provides error handling context for route-based error pages.
 *
 * Wrap your application with this provider to enable centralized error handling.
 * Allows child components to trigger an error page with custom details,
 * and to clear the error state.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - The child components to be wrapped by the provider.
 * @returns {JSX.Element} The context provider wrapping its children.
 */
export function ErrorRouteProvider({ children }) {
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // DEBUG
  // useEffect(() => {
  //   console.log("ErrorRouteProvider MOUNT");
  //   return () => console.log("...UNMOUNT");
  // }, []);

  const showErrorPage = useCallback(
    (payload = {}) => {
      const withDefaults = {
        title: "Something went wrong",
        message: "An unexpected error occurred.",
        status: undefined,
        detail: undefined, // arbitrary object (e.g., server JSON)
        backTo: location.pathname + location.search + location.hash,
        onRetry: undefined, // optional: () => Promise<void>
        ...payload,
      };
      console.log(withDefaults);
      setError(withDefaults);
      navigate("/error", { replace: false });
    },
    [navigate, location]
  );

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo(
    () => ({ error, showErrorPage, clearError }),
    [error, showErrorPage, clearError]
  );
  return (
    <ErrorRouteContext.Provider value={value}>
      {children}
    </ErrorRouteContext.Provider>
  );
}

export function useRouteError() {
  const ctx = useContext(ErrorRouteContext);
  if (!ctx)
    throw new Error("useRouteError must be used within <ErrorRouteProvider>");
  return ctx;
}
